// POS kataloq dəyişiklik bildirişçisi — YALNIZ POS rejimi (pos_local).
//
// Admin (cloud) məhsulun qiymətini/menyu statusunu dəyişəndə dəyişiklik cloud →
// pos_local pull worker ilə lokala düşür və pos_local `branch:<id>` WS kanalında
// `catalog.*` event-i yayır (bax: rezmo_pos_local pull.ts → toLocalNotification).
//
// Bu modul həmin event-i tutub:
//   1. pos_local kataloqunu refetch edir və əvvəlki snapshot ilə diff edir,
//   2. qiymət/status dəyişən məhsul üçün POS işçisinə toast bildirişi göstərir
//      ("«kükü» qiyməti dəyişdi: 9.50 → 7.50 ₼"),
//   3. `pos:catalog-changed` window event-i yayır ki, açıq menyu səhifəsi
//      (PosTablePage) məhsulları reload-suz yenidən çəksin.
//
// WS notification payload-ı yalnız {resourceId, revision} daşıyır (ad/qiymət yox),
// ona görə dəqiq ad+qiymət üçün kataloqu yenidən çəkib diff edirik.

import { toast } from '../../components/ui/notify';

import { POS_ENABLED } from './config';
import { getPosWs, PosEnvelope } from './posWs';
import { posRequest } from './posClient';
import { parseI18nName } from './catalogMappers';
import { addSystemNotification } from './systemNotifications';
import { tStatic } from '../../i18n/I18nContext';

interface PosCatalogProduct {
  id: string;
  name: string;
  priceCents: number;
  isActive?: boolean;
}

interface Snap {
  name: string;
  priceCents: number;
  isActive: boolean;
}

let installed = false;
let seeded = false;
let snapshot = new Map<string, Snap>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const DEBOUNCE_MS = 300;

function fmtPrice(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Həm ani toast, həm də Bildirişlər səhifəsindəki "Sistem" tabına qalıcı bildiriş. */
function notify(title: string, message: string): void {
  toast.info(message);
  addSystemNotification({ title, message });
}

async function fetchProducts(): Promise<PosCatalogProduct[]> {
  const res = await posRequest<PosCatalogProduct[]>({
    method: 'GET',
    path: '/v1/catalog/products',
  });
  return res.ok && Array.isArray(res.data) ? res.data : [];
}

function toSnap(p: PosCatalogProduct): Snap {
  return {
    name: parseI18nName(p.name),
    priceCents: p.priceCents,
    isActive: p.isActive !== false,
  };
}

/** Kataloqu çək; ilk dəfə yalnız snapshot qur (toast yox), sonrakı dəfələr diff et. */
async function refreshAndDiff(): Promise<void> {
  let products: PosCatalogProduct[];
  try {
    products = await fetchProducts();
  } catch {
    return; // pos_local əlçatmazdırsa səssiz keç — növbəti event yenidən cəhd edər
  }
  if (products.length === 0) return;

  const next = new Map<string, Snap>();
  for (const p of products) next.set(p.id, toSnap(p));

  // İlk seed — bildiriş göstərmirik, sadəcə baza vəziyyəti qururuq.
  if (!seeded) {
    snapshot = next;
    seeded = true;
    return;
  }

  for (const [id, cur] of next) {
    const prev = snapshot.get(id);
    if (!prev) {
      if (cur.isActive) {
        notify(
          tStatic('catalog.newProduct.title'),
          tStatic('catalog.newProduct.msg', { name: cur.name }),
        );
      }
      continue;
    }
    if (prev.priceCents !== cur.priceCents) {
      notify(
        tStatic('catalog.priceChanged.title'),
        tStatic('catalog.priceChanged.msg', {
          name: cur.name,
          from: fmtPrice(prev.priceCents),
          to: fmtPrice(cur.priceCents),
        }),
      );
    } else if (prev.isActive !== cur.isActive) {
      notify(
        tStatic(cur.isActive ? 'catalog.added.title' : 'catalog.removed.title'),
        tStatic(cur.isActive ? 'catalog.added.msg' : 'catalog.removed.msg', {
          name: cur.name,
        }),
      );
    }
  }

  snapshot = next;

  // Açıq menyu səhifəsinə "yenidən çək" siqnalı (qiymət/şəkil/status reload-suz yenilənsin).
  window.dispatchEvent(new CustomEvent('pos:catalog-changed'));
}

/**
 * POS rejimində kataloq dəyişiklik bildirişçisini qur (index.tsx, render-dən əvvəl).
 * pos_local WS-ə app-boyu qoşulu qalır ki, istifadəçi hansı səhifədə olursa-olsun
 * qiymət dəyişiklikləri bildiriş kimi düşsün.
 */
export function installCatalogNotifier(): void {
  if (installed || !POS_ENABLED) return;
  if (typeof window === 'undefined') return; // SSR/test mühiti
  installed = true;

  const ws = getPosWs();
  ws.acquire(); // ref-counted — WS-i app boyu açıq saxla

  // Başlanğıc snapshot (cari qiymətlər) — ilk real dəyişiklik düzgün diff olunsun.
  void refreshAndDiff();

  ws.onEvent((env: PosEnvelope) => {
    if (typeof env.type !== 'string' || !env.type.startsWith('catalog.')) return;
    // Burst-ları (bir neçə event ardıcıl) tək refetch-ə birləşdir.
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void refreshAndDiff();
    }, DEBOUNCE_MS);
  });
}
